import { useCallback, useState } from "react";

/**
 * Collapse state that survives a page load.
 *
 * A collapsed section is a standing preference — *I don't work this group* —
 * not a transient view state. Re-collapsing the same sections on every visit
 * is the friction that ends with nobody collapsing anything, which is how a
 * long page stays long.
 *
 * `localStorage` rather than the URL, deliberately: this is a fact about the
 * reader, not about the thing being read. A link pasted into Slack should open
 * the way the recipient keeps their own page, not the way the sender left
 * theirs.
 *
 * Every read and write is guarded. `localStorage` throws in private-mode
 * Safari and when the origin's quota is full, and a preference must never take
 * a page down with it — on any failure the default simply stands.
 */

const PREFIX = "th:collapsed:";

function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* a preference is never worth an error */
  }
}

/**
 * One collapsible section: its state, a toggle, and an explicit setter for
 * the callers that open a section as a side effect of something else (running
 * a diagnosis opens the pipeline it animates). The setter persists too — an
 * opened section stays opened, however it was opened.
 */
export function useStickyCollapsed(
  key: string,
  defaultCollapsed = false,
): [boolean, () => void, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = read(key);
    return typeof stored === "boolean" ? stored : defaultCollapsed;
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      write(key, next);
      return next;
    });
  }, [key]);

  const set = useCallback(
    (next: boolean) => {
      write(key, next);
      setCollapsed(next);
    },
    [key],
  );

  return [collapsed, toggle, set];
}

/**
 * A set of collapsible siblings under ONE storage key — the fleet table's
 * status groups. Stored as an array of the collapsed members, so a group that
 * is later renamed or removed simply stops matching rather than resurrecting
 * as a stale boolean.
 *
 * `defaults` names members that start collapsed, and is applied **only when
 * the key is absent from storage** — the first visit, and nothing after it.
 * That distinction is the whole of it: this hook stores the collapsed MEMBERS,
 * so once the key exists an empty array and a never-touched key are
 * indistinguishable. Seeding unconditionally would re-collapse the group on
 * every load after an operator expanded it, which is "cannot stay expanded"
 * wearing "collapsed by default" as a costume. Seeding on first use alone
 * gives a default-collapsed group whose expansion is remembered, matching the
 * `useStickyCollapsed(key, true)` precedent.
 */
export function useStickyCollapsedSet<T extends string>(
  key: string,
  defaults: readonly T[] = [],
): [Set<T>, (member: T) => void] {
  const [collapsed, setCollapsed] = useState<Set<T>>(() => {
    const stored = read(key);
    if (Array.isArray(stored)) {
      return new Set(stored.filter((m): m is T => typeof m === "string"));
    }
    // No key yet (first visit, cleared site data, or a read that threw). The
    // defaults stand, and are deliberately NOT written back — writing here
    // would make the next load indistinguishable from an operator having
    // collapsed them by hand, and there is nothing to preserve until one does.
    return new Set(defaults);
  });

  const toggle = useCallback(
    (member: T) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(member)) next.delete(member);
        else next.add(member);
        // `Array.from`, not a spread: this project targets ES5, where
        // spreading a Set needs `downlevelIteration`.
        write(key, Array.from(next));
        return next;
      });
    },
    [key],
  );

  return [collapsed, toggle];
}
